import {
  SocialLinks as SL,
  Config,
  Profile,
  TYPE_DESKTOP,
  TYPE_MOBILE,
  TYPE_DEFAULT
} from 'social-links';

const config: Config = {
  usePredefinedProfiles: false,
  allowQueryParams: true,
  trimInput: true
};

export enum ESocialUrl {
  FACEBOOK = 'https://facebook.com/',
  INSTAGRAM = 'https://instagram.com/'
}

export enum SOCIAL_PROFILE_NAMES {
  WEB_SITE = 'web-site',
  FACEBOOK = 'facebook',
  INSTAGRAM = 'instagram',
  TWITTER = 'twitter',
  YOUTUBE = 'youtube',
  TIKTOK = 'tiktok',
  PINTEREST = 'pinterest'
}

const PROFILES: Profile[] = [
  {
    name: SOCIAL_PROFILE_NAMES.FACEBOOK,
    matches: [
      {
        match: '(https?://)?(www.)?facebook.com/({PROFILE_ID})/?',
        group: 3,
        type: TYPE_DESKTOP,
        pattern: 'https://facebook.com/{PROFILE_ID}'
      },
      {
        match: '(https?://)?m.facebook.com/({PROFILE_ID})/?',
        group: 2,
        type: TYPE_MOBILE,
        pattern: 'https://m.facebook.com/{PROFILE_ID}'
      },
      { match: '(https?://)?(www.)?facebook.com/({PROFILE_ID})/?', group: 1 }
    ]
  },
  {
    name: SOCIAL_PROFILE_NAMES.INSTAGRAM,
    matches: [
      {
        match: '(https?://)?(www.)?instagram.com/({PROFILE_ID})/?',
        group: 3,
        type: TYPE_DESKTOP,
        pattern: 'https://instagram.com/{PROFILE_ID}'
      },
      {
        match: '(https?://)?m.instagram.com/({PROFILE_ID})/?',
        group: 2,
        type: TYPE_MOBILE,
        pattern: 'https://m.instagram.com/{PROFILE_ID}'
      },
      { match: '@?({PROFILE_ID})', group: 1 }
    ]
  },
  {
    name: SOCIAL_PROFILE_NAMES.TWITTER,
    matches: [
      {
        match: '(https?://)?(www.)?twitter.com/@?({PROFILE_ID})/?',
        group: 3,
        type: TYPE_DESKTOP,
        pattern: 'https://twitter.com/{PROFILE_ID}'
      },
      {
        match: '(https?://)?mobile.twitter.com/@?({PROFILE_ID})/?',
        group: 2,
        type: TYPE_MOBILE,
        pattern: 'https://mobile.twitter.com/{PROFILE_ID}'
      },
      { match: '@?({PROFILE_ID})', group: 1 }
    ]
  },
  {
    name: SOCIAL_PROFILE_NAMES.YOUTUBE,
    matches: [
      {
        match: '(https?://)?(www.)?youtube.com/@({PROFILE_ID})/channels',
        group: 3,
        pattern: 'https://youtube.com/@{PROFILE_ID}/channels'
      },
      {
        match: '(https?://)?(www.)?youtube.com/@({PROFILE_ID})/?',
        group: 3,
        pattern: 'https://youtube.com/@{PROFILE_ID}'
      },
      {
        match: '(https?://)?(www.)?youtube.com/c/({PROFILE_ID})/?',
        group: 3,
        pattern: 'https://youtube.com/c/{PROFILE_ID}'
      },
      {
        match: '(https?://)?(www.)?youtube.com/channel/({PROFILE_ID})/?',
        group: 3,
        type: TYPE_DESKTOP,
        pattern: 'https://youtube.com/channel/{PROFILE_ID}'
      },
      {
        match: '(https?://)?m.youtube.com/c/({PROFILE_ID})/?',
        group: 2,
        type: TYPE_MOBILE,
        pattern: 'https://m.youtube.com/c/{PROFILE_ID}'
      },
      {
        match: '(https?://)?(www.)?youtube.com/channel/({PROFILE_ID})/?',
        group: 1
      }
    ]
  },
  {
    name: SOCIAL_PROFILE_NAMES.TIKTOK,
    matches: [
      {
        match: '(https?://)?(www.)?tiktok.com/@({PROFILE_ID})/?',
        group: 3,
        type: TYPE_DESKTOP,
        pattern: 'https://tiktok.com/@{PROFILE_ID}'
      },
      {
        match: '(https?://)?(www.)?tiktok.com/@({PROFILE_ID})/?',
        group: 2,
        type: TYPE_MOBILE,
        pattern: 'https://tiktok.com/@{PROFILE_ID}'
      },
      { match: '(https?://)?(www.)?tiktok.com/@({PROFILE_ID})/?', group: 1 }
    ]
  },
  {
    name: SOCIAL_PROFILE_NAMES.PINTEREST,
    matches: [
      {
        match: '(https?://)?([a-z]{1,3}.)?pinterest.com/({PROFILE_ID})/?',
        group: 3,
        type: TYPE_DESKTOP,
        pattern: 'https://pinterest.com/{PROFILE_ID}'
      },
      {
        match: '(https?://)?((co|www).)?pinterest.com/({PROFILE_ID})/?',
        group: 3,
        type: TYPE_DESKTOP,
        pattern: 'https://pinterest.com/{PROFILE_ID}'
      },
      {
        match: '(https?://)?((co|www).)?pinterest.com/({PROFILE_ID})/?',
        group: 2,
        type: TYPE_MOBILE,
        pattern: 'https://pinterest.com/{PROFILE_ID}'
      },
      {
        match: '(https?://)?((co|www).)?pinterest.com/({PROFILE_ID})/?',
        group: 1
      }
    ]
  }
];

export class SocialLinks {
  private socialLink = new SL(config);

  private webSiteRegex = RegExp(
    `(http(s)?:\\/\\/.)?(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{2,256}\\.[a-z]{2,6}\\b([-a-zA-Z0-9@:%_\\+.~#?&//=]*)`,
    'g'
  );

  constructor() {
    PROFILES.map(({ name, matches }) =>
      this.socialLink.addProfile(name, matches)
    );
  }

  isValid(profileName: SOCIAL_PROFILE_NAMES, link: string): boolean {
    if (profileName === SOCIAL_PROFILE_NAMES.WEB_SITE) {
      return this.webSiteRegex.test(link);
    }
    return this.socialLink.isValid(profileName, link);
  }

  sanitize(profileName: string, link: string, type = TYPE_DEFAULT): string {
    if (profileName === SOCIAL_PROFILE_NAMES.WEB_SITE) {
      return link;
    }
    return this.socialLink.sanitize(profileName, link, type);
  }

  getProfileId(profileName: SOCIAL_PROFILE_NAMES, link: string) {
    return this.socialLink.getProfileId(profileName, link);
  }
}

export function getSocialProfile(
  socialName: SOCIAL_PROFILE_NAMES,
  link?: string | null
) {
  const socialLinks = new SocialLinks();

  const transformedLink = (link || '').replace('@', '');

  if (socialLinks.isValid(socialName, transformedLink)) {
    return socialLinks.getProfileId(socialName, transformedLink);
  }

  return transformedLink;
}

export default SocialLinks;
